import React, { useState, useMemo, useEffect } from "react";
import styled from '@emotion/styled';
import SelectWithLabel, { YesNoSelect } from "../components/Inputs/Select";
import { isMobile } from 'react-device-detect';
import EyeTicketLogo from '../assets/EyeTicket_Logo.png';
import TextInputWithLabel from "../components/Inputs/TextInput";
import Table, { SelectColumnFilter } from "../components/Ui/Table";
import { Alert } from "../components/Ui/Alert";
import { fetchAccount, fetchTitles } from "../http/account";
import { fetchUserFrames } from "../http/frames";
import CheckboxWithLabel from "../components/Inputs/Checkbox";
import { updateEyeTicket, createEyeTicket } from "../http/eyeticket";
import { fetchLinkedAccounts } from "../http/account";
import { fetchFavouriteOpticians, fetchOpticiansByPostcode, addFavouriteOptician, removeFavouriteOptician } from "../http/optician";
import { LoadingScreen } from "../components/StyledComponents/Forms/Loader";
import { authenticateSelfRaise } from "../http/auth";
import { fetchFrames, createSelfRaiseEyeTicket } from "../http/selfRaise";
import { useNavigate, useLocation } from "react-router-dom";

const WarningBox = styled.div`
  background-color: #fff3cd;
  border-left: 8px solid #ffeeba;
  padding: 15px;
  margin-bottom: 20px;
`;

const SmallModalHeader = styled.h3`
  margin-top: 0;
  color: #333;
  text-align: center;
`;

const Logo = styled.img`
  height: auto;
  width: 120px;
  margin-right: 20px;
`;

const CenterTextContainer = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right:130px;
`;

const StepsHeader = styled.div`
  background-color: ${({ backGroundColour }) => backGroundColour};
  color: ${({ fontColour }) => fontColour};
  display: flex;
  align-items: center;
  padding: 20px;
  box-shadow: 0 4px 4px -2px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ModalBody = styled.div`
  max-height: ${isMobile ? '500px' : '700px'};
  overflow-y: auto;
  width:100%;

  padding: 0px 40px ${isMobile ? "0px" : "30px"} 40px;

  flex-grow: 1;
  & > div { 
    margin-top: 0;
  }
`;

const ModalFooter = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding: 10px 0px;
`;

const Step = styled.div`
  width: 50px;
  height: 30px;
  line-height: 30px;
  border-radius: 15px;
  background: #cccccc;
  color: white;
  text-align: center;
  font-size: 0.9em;
`;

const ActiveStep = styled(Step)`
  background-color: #4CAF50;
`;

const StepLabel = styled.span`
  display: inline-block;
  padding: 0 ${isMobile ? '10px' : '15px'};;
  font-weight: bold;
  color: ${({ fontColour }) => fontColour};
`;

const StepContainer = styled.div`
  display: flex;
  align-items: center;
  ${(props) => (props.right ? "right: 20px;" : "left: 20px;")}
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const FieldContainer = styled.div`
  flex: 1;
  min-width: 120px;
  
  &:first-of-type {
    margin-left: 0;
  }
`;

const Button = styled.button`
  background-color: #93C572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698D51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
`;

const Container = styled.div`
  max-width: 950px;

  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  background: #fff;

  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #f0f0f0;
`;


const defaultTicket = {
  eticketstatus: "Open",
  eticket_barcode: "0",
  eticketaccountcode: "",
  eticketgenemail: "",
  eticketsafetyorvdu: "",
  eticket_frame: "",
  eticket_lockframe: "",
  eticketlensmaterial: "",
  eticket_lenstype: "",
  etickettitle: "",
  eticketchrist: "",
  eticketsurn: "",
  eticketpatientemail: "",
  eticketpatientmobile: "",
  eticketclockno: "",
  eticketdepartment: "",
  eticketorderno: "",
  eticketsite: "",
  eticketcostcent: "",
  eticket_sighttest: 0,
  eticket_STAerospace: "",
  eticket_Ishihara_Edition: "",
  eticket_Ishihara_Plates: "",
  eticket_Ishihara: "",
  eticket_R_jaeger: "",
  eticket_L_jaeger: "",
  eticket_B_jaeger: "",
  eticket_j1_Jaeger: "",
  eticket_Jaeger_By: "",
  eticket_aero_notes: "",
  eticketdate: "",
  eticketdatemodified: "",
  eticket_clinicID: "",
  eticket_clinicappointment: "",
  OpticianID: "",
  eticket_acknowledge: 0,
  eticket_dse_aio: "",
  eticket_type: "",
  eticket_OpenCode: "",
  eticket_VoucherLocation: "",
  eticket_PatientReport: "",
  eticket_royalmail: "",
  eticket_ExpireReason: "",
  eticket_ClinicCovid: "",
  eticket_ClinicCovid_Updated: "",
  statusDescription: "",
  message: ""
};

export default function SelfRaiseEyeTicket() {
  const [loading, setLoading] = useState(false);

  const stepTitles = ["1", "2", "3", "4"];

  const stepDescriptions = ["Managers Details", "Eyewear", `Employee ${!isMobile ? "Details" : ""}`, `${isMobile ? "Optician" : "Select an Optician"}`];

  const [currentStep, setCurrentStep] = useState(0);

  const [titles, setTitles] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const [ticketDetails, setTicketDetails] = useState(defaultTicket);
  const [missingFields, setMissingFields] = useState({});
  const [invalidData, setInvalidData] = useState(null);
  const [invalidContactData, setInvalidContactData] = useState(null);
  const [contactPref, setContactPref] = useState(ticketDetails.eticket_acknowledge === 2 ? 'Email' : 'Text Message');
  const [account, setAccount] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userFrames, setUserFrames] = useState([]);
  const [optionalState, setOptionalState] = useState({ userSelectedFrame: (ticketDetails.eticket_frame === '' ? false : true) })
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [opticians, setOpticians] = useState([]);
  const [postcode, setPostcode] = useState([]);
  const [isValidPostcode, setIsValidPostcode] = useState(true);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [managersDetails, setManagersDetails] = useState({
    email: '',
    surname: ''
  });

  const navigate = useNavigate();
  const location = useLocation();

  const colours = {
    sidebar: '#333A58',
    menuContent: '#2D344F',
    hoverColour: '#585F7C',
    font: '#BAC3CB',
    appName: 'MCR Eye Ticket',
    image: '',
    isDefault: true,
    themeId: 0
  };

  const setDefaultValues = () => {
    if (!ticketDetails.etickunid) {
      const d = new Date();
      let stringDate = d.toISOString();

      if (account) {
        const initialValues = {
          eticketaccountcode: linkedAccounts[0].value,
          eticketsafetyorvdu: (ticketDetails?.eticketsafetyorvdu !== "" && ticketDetails?.eticketsafetyorvdu !== undefined) ? ticketDetails.eticketsafetyorvdu : (frameTypes[0]?.value || ""),
          eticketlensmaterial: (lensMaterials[0]?.value || ""),
          eticket_barcode: "0",
          eticket_lockframe: 0,
          eticket_frame: "",
          eticketdate: stringDate,
          eticketdatemodified: stringDate,
          eticket_clinicappointment: "2000-01-01T00:00:00",
          eticket_ClinicCovid_Updated: "0001-01-01T00:00:00",
          eticketorderno: (account.contract_no !== "0" ? account.contract_no : ""),
          eticket_lenstype: "optional",
          eticketpatientmobile: "",
          eticket_acknowledge: 0,
          eticketgenemail: managersDetails.email,
          eticket_STAerospace: 0,
          eticket_Ishihara_Edition: 0,
          eticket_Ishihara_Plates: 0,
          eticket_sighttest: 0,
          eticket_Ishihara: "0",
          eticket_R_jaeger: "-",
          eticket_L_jaeger: "-",
          eticket_B_jaeger: "-",
          eticket_j1_Jaeger: "-",
          eticket_Jaeger_By: "-",
          eticket_aero_notes: "-",
          eticket_clinicID: "-",
          eticket_dse_aio: 0,
          eticket_type: 0,
          eticket_OpenCode: "-",
          eticket_VoucherLocation: "-",
          eticket_PatientReport: "-",
          eticket_royalmail: "-",
          eticket_ExpireReason: 0,
          eticket_ClinicCovid: 0,
          OpticianID: null,
        };

        setTicketDetails(prevState => ({ ...prevState, ...initialValues }));
      }
    }
  };

  const loadOpticians = async () => {
    if (isAuthenticated) {
      try {
        const opticiansResponse = await fetchOpticiansByPostcode(postcode, account.dispensing_Fee_Max);
        const favouriteOpticiansResponse = await fetchFavouriteOpticians(account.dispensing_Fee_Max);

        if (opticiansResponse.length > 0) {
          setIsLoading(true);
          const favouriteOpticians = favouriteOpticiansResponse.map(optician => ({
            ...optician,
            type: "Favourite"
          }));

          const opticians = opticiansResponse.map(optician => ({
            ...optician,
            type: "Local"
          }));

          const mergedOpticians = [...opticians, ...favouriteOpticians];

          const opticianMap = new Map();
          mergedOpticians.forEach(optician => {
            const existing = opticianMap.get(optician.unqID);
            if (!existing || (existing && existing.type == "Local" && optician.type == "Favourite")) {
              opticianMap.set(optician.unqID, optician);
            }
          });

          const uniqueMergedOpticians = Array.from(opticianMap.values());

          setOpticians(uniqueMergedOpticians);
          setIsValidPostcode(true);
        } else {
          setIsValidPostcode(false);
        }
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadOpticians();
  }, [postcode]);

  useEffect(() => {
    const loadUserFrames = async () => {
      setIsLoading(true);
      try {
        const frames = await fetchFrames();
        setUserFrames(frames);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts(managersDetails.email);
        const userAccounts = [];

        accounts.forEach(account => {
          userAccounts.push({ label: account.accountdescrip, value: account.accountid });
        });

        setLinkedAccounts(userAccounts);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadAccount = async () => {
      setIsLoading(true);
      try {

        const fetchedAccount = await fetchAccount();
        setAccount(fetchedAccount.Users_Profile);
        setPostcode((fetchedAccount.Users_Profile.copostcode ? fetchedAccount.Users_Profile.copostcode : 'WS98BZ'));

        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadTitles = async () => {
      setIsLoading(true);
      try {
        const titles = await fetchTitles();

        const restructuredTitles = [{ label: 'Select a title..', value: '' }];

        restructuredTitles.push(...titles.map(title => ({
          label: title.title,
          value: title.title
        })));

        setTitles(restructuredTitles);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated == true) {
      loadAccount();
      loadUserFrames();
      loadLinkedAccounts();
      loadTitles();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isLoading && linkedAccounts.length > 0) {
      setDefaultValues();
    }
  }, [linkedAccounts, account]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+?(\d.*){3,}$/;

  const mobileOrEmail = [
    { label: 'Text Message', value: 'Text Message' },
    { label: 'Email', value: 'Email' }
  ];

  const frameTypes = [];

  if (account.safetyorvdu === 'safevdu') {
    frameTypes.push({ label: 'Safety', value: 'Safety' });
    frameTypes.push({ label: 'DSE', value: 'VDU' });
  } else if (account.safetyorvdu === 'safe') {
    frameTypes.push({ label: 'Safety', value: 'Safety' });
  } else if (account.safetyorvdu === 'vdu') {
    frameTypes.push({ label: 'DSE', value: 'VDU' });
  }

  const lensMaterials = [];

  if (account.lensmaterial === 'crandpoly') {
    lensMaterials.push({ label: 'Polycarbonate', value: 'poly' });
    lensMaterials.push({ label: 'CR39', value: 'cr39' });
  } else if (account.lensmaterial === 'cr39') {
    lensMaterials.push({ label: 'CR39', value: 'cr39' });
  } else {
    lensMaterials.push({ label: 'Polycarbonate', value: 'poly' });
  }


  const lensTypes = [{ label: 'Select a lens', value: 'optional' }];

  if (account.lenstype === 'svbfvf') {
    lensTypes.push({ label: 'Single Vision', value: 'Single Vision' });
    lensTypes.push({ label: 'Bi-Focal', value: 'D28 Bifocal' });
    lensTypes.push({ label: 'Vari-Focal', value: 'Progressive' });
  } else if (account.lenstype === 'svbf') {
    lensTypes.push({ label: 'Single Vision', value: 'Single Vision' });
    lensTypes.push({ label: 'Bi-Focal', value: 'D28 Bifocal' });
  } else {
    lensTypes.push({ label: 'Single Vision', value: 'Single Vision' });
  }

  const frames = [{ label: 'Select a frame', value: '' }];

  userFrames.forEach(userFrame => {
    frames.push({ label: userFrame.framename, value: userFrame.framename });
  });

  async function authenticateManagersDetails() {
    setIsLoading(true);
    try {
      const response = await authenticateSelfRaise(managersDetails.email, managersDetails.surname);
      setError(null);
      return response;
    } catch (e) {
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  async function nextStep() {
    if (currentStep === 0) {
      const isAuthenticated = await authenticateManagersDetails();
      if (!isAuthenticated) {
        setError({ message: 'Authentication failed. Please check the details and try again.' });
        return;
      } else {
        setIsAuthenticated(true);
      }
    }
    if (validateForm()) {
      if (currentStep < stepTitles.length - 1) {
        setCurrentStep(currentStep + 1);
        loadOpticians();
      }
    }
  };

  const prevStep = () => {
    setError(null);
    validateForm();

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
    if (currentStep === 99) {
      setCurrentStep(2);
    }
  };

  const renderSteps = stepTitles.map((number, index) => (
    <StepContainer key={number}>
      {(currentStep) === index ? <ActiveStep>{number}</ActiveStep> : <Step>{number}</Step>}
      <StepLabel fontColour={colours.font}>{stepDescriptions[index]}</StepLabel>
    </StepContainer>
  ));

  const isOpticianSelected = (id) => {
    return id === ticketDetails.OpticianID;
  };

  const columns = React.useMemo(
    () => {
      const allColumns = [
        {
          Header: "",
          accessor: "select",
          Cell: ({ row }) => {
            const OpticianID = row.original.unqID;

            return (
              <input
                type="checkbox"
                checked={isOpticianSelected(OpticianID)}
                onChange={() => handleEnteredValues('OpticianID', OpticianID)}
              />
            );
          }
        },
        {
          Header: "Practice Name",
          accessor: "companyname",
        },
        {
          Header: "Address",
          accessor: "FullAddress",
        },
        {
          Header: "Distance (miles)",
          accessor: "DistanceFromAddress",
        },
        {
          Header: "Dispensing",
          accessor: "DispensingFees",
        },
        {
          Header: "Phone",
          accessor: "cotelephone",
        },
        {
          Header: "Favourite",
          accessor: "type",
          Filter: SelectColumnFilter,
          filter: 'includes',
          Cell: ({ row }) => {
            const isFavourite = row.original.type;

            return (
              <Button style={{ fontSize: "12px" }} onClick={() => favouriteOptician(row.original, isFavourite === 'Favourite')}>
                {isFavourite === 'Local' ? 'Favourite' : 'Un-Favourite'}
              </Button>
            );
          }
        }
      ];

      const mobileColumns = allColumns.filter(column => {
        if (isMobile && (column.accessor === 'dispensingFees' || column.accessor === 'distanceFromAddress' || column.accessor === 'cotelephone' || column.accessor === 'Favourite')) {
          return false;
        }
        return true;
      });

      return isMobile ? mobileColumns : allColumns;
    },
    [isMobile, selectedRow, ticketDetails]
  );

  async function favouriteOptician(optician, bRemove) {
    setIsLoading(true);
    try {

      if (bRemove) {
        await removeFavouriteOptician(optician.unqID);
      } else {
        const newFavouriteOptician = {
          opticianID: optician.unqID,
          enduser: managersDetails.email,
          enduser_co_name: account.companyname,
          opticians_onsite_ID: optician.unqID
        }

        await addFavouriteOptician(newFavouriteOptician, (ticketDetails.eticketaccountcode ? ticketDetails.eticketaccountcode : linkedAccounts[0].value));
      }

      loadOpticians();
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }

  const initialState = { hiddenColumns: ['type'] };

  const validateForm = () => {
    let newMissingFields = {};
    let newInvalidData = {};
    let isValid = true;

    const fieldLengthChecks = {
      eticketsite: 25,
      eticketsurn: 30,
      eticketchrist: 20,
      eticketclockno: 15,
      eticketcostcent: 25,
      eticketdepartment: 30,
    };


    if (account.lentype_force === 1 && currentStep === 1 && ticketDetails.eticket_lenstype === 'optional') {
      newMissingFields.eticket_lenstype = true;
    }
    if (account.frameselect === 1 && currentStep === 1 && ticketDetails.eticket_frame === '') {
      newMissingFields.eticket_frame = true;
    }
    if (!ticketDetails.etickettitle && currentStep === 2) {
      newMissingFields.etickettitle = true;
    }
    if (!ticketDetails.eticketchrist && currentStep === 2) {
      newMissingFields.eticketchrist = true;
    }
    if (!ticketDetails.eticketsurn && currentStep === 2) {
      newMissingFields.eticketsurn = true;
    }
    if (account.ordernumber === 1 && currentStep === 2 && !ticketDetails.eticketorderno) {
      newMissingFields.eticketorderno = true;
    }
    if (account.clockno_req === 1 && currentStep === 2 && !ticketDetails.eticketclockno) {
      newMissingFields.eticketclockno = true;
    }
    if (account.costcentre_req === 1 && currentStep === 2 && !ticketDetails.eticketcostcent) {
      newMissingFields.eticketcostcent = true;
    }
    if (account.department_req === 1 && currentStep === 2 && !ticketDetails.eticketdepartment) {
      newMissingFields.eticketdepartment = true;
    }
    if (account.nameofsite_req === 1 && currentStep === 2 && !ticketDetails.eticketsite) {
      newMissingFields.eticketsite = true;
    }
    if (!ticketDetails.OpticianID && currentStep === 3) {
      newMissingFields.OpticianID = true;
    }

    if (currentStep === 2) {
      isContactFieldValid(newMissingFields);

      for (let field in fieldLengthChecks) {
        if (ticketDetails[field] && ticketDetails[field].length > fieldLengthChecks[field]) {
          newInvalidData[field] = `Exceeds maximum length of ${fieldLengthChecks[field]}`;
          isValid = false;
        }
      }
    }

    for (let key in newMissingFields) {
      if (newMissingFields[key] === true) {
        isValid = false;
        break;
      }
    }

    setMissingFields(newMissingFields);
    setInvalidData(newInvalidData);
    return isValid;
  };


  function handleEnteredValues(identifier, value) {
    setTicketDetails((prevValues) => ({
      ...prevValues,
      [identifier]: value
    }));
  }

  function handleOptionalState(identifier, value) {
    setOptionalState((prevValues) => ({
      ...prevValues,
      [identifier]: value
    }));
  }

  function handleFrameChange(value) {
    handleEnteredValues('eticket_frame', value);
    handleOptionalState('userSelectedFrame', (value === '' ? false : true));
  }

  function setManager(field, value) {
    setManagersDetails((prevValues) => ({
      ...prevValues,
      [field]: value
    }));
  }

  const handleSaveEyeTicket = async ({ }) => {
    let isComplete = true;
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    if (ticketDetails.eticketpatientmobile && contactPref === 'Text Message') {
      ticketDetails.eticket_acknowledge = 1;
      ticketDetails.eticketpatientemail = '';
    } else if (ticketDetails.eticketpatientemail && contactPref === 'Email') {
      ticketDetails.eticket_acknowledge = 2;
      ticketDetails.eticketpatientmobile = '';
    } else {
      ticketDetails.eticket_acknowledge = 0;
      ticketDetails.eticketpatientemail = '';
      ticketDetails.eticketpatientmobile = '';
    }

    if (frameTypes.length === 1) {
      ticketDetails.eticketsafetyorvdu = frameTypes[0].value;
    }
    if (lensMaterials.length === 1) {
      ticketDetails.eticketlensmaterial = lensMaterials[0].value;
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsLoading(true);
    let response = null;

    try {
      ticketDetails.eticketstatus = 'Authorise';

      response = await createSelfRaiseEyeTicket(ticketDetails);
      setError(null);
    } catch (e) {
      isComplete = false;
      setError(e);
    } finally {
      setIsLoading(false);
      if (isComplete && !error) {
        if(response)
        {
          navigate(`/selfraise-complete?eyeTicketId=${response}&fullName=${ticketDetails.etickettitle} ${ticketDetails.eticketchrist} ${ticketDetails.eticketsurn}`);
        }
      }
    }
  };

  function isContactFieldValid(newMissingFields) {
    if (contactPref === 'Email') {
      if (ticketDetails.eticketpatientemail && !emailRegex.test(ticketDetails.eticketpatientemail)) {
        newMissingFields.eticketpatientemail = true;
        setInvalidContactData("Invalid Email Address");
      }
    } else {
      if (ticketDetails.eticketpatientmobile && !phoneRegex.test(ticketDetails.eticketpatientmobile)) {
        newMissingFields.eticketpatientmobile = true;
        setInvalidContactData("Invalid Phone Number");
      }
    }
  }

  const textInputContactField = contactPref === 'Email' ? ticketDetails.eticketpatientemail : ticketDetails.eticketpatientmobile;
  const missingContactField = contactPref === 'Email' ? missingFields.eticketpatientemail : missingFields.eticketpatientmobile;


  return (
    <Wrapper>
      <Container>
        <StepsHeader backGroundColour={colours.sidebar} fontColour={colours.font}>
          {!isMobile && <Logo src={EyeTicketLogo} alt="EyeTicket Logo" />}
          <CenterTextContainer>
            {renderSteps}
          </CenterTextContainer>
        </StepsHeader>
        {error && !isLoading && <Alert type="error"><b>{error.message}</b></Alert>}
        {isLoading &&
          <ModalBody>
            <LoadingScreen
              value="Please wait whilst we process your EyeTicket, this may take a few moments.."
              size="60px"
              borderColor="#d3d3d3"
              topColor={colours.sidebar}
            />
          </ModalBody>
        }
        {!isLoading &&
          <ModalBody>
            {currentStep === 0 &&
              <div>
                <Heading>Managers Details</Heading>
                <Paragraph>Please provide your managers email and surname to continue.</Paragraph>
                <Row>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='Email Address'
                      required={true}
                      value={managersDetails.email}
                      onChange={(event) => setManager('email', event.target.value)}
                      tabIndex={1} />
                  </FieldContainer>
                </Row>
                <Row>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='Surname'
                      required={true}
                      value={managersDetails.surname}
                      onChange={(event) => setManager('surname', event.target.value)}
                      tabIndex={2} />
                  </FieldContainer>
                </Row>
              </div>
            }
            {currentStep === 1 &&
              <div>
                <Heading>Eyewear</Heading>
                <Paragraph>Please provide details on the type of eyewear required. Any fields that are left blank will be decided upon by the optician.</Paragraph>
                <SelectWithLabel
                  label="Frame Type"
                  options={frameTypes}
                  value={ticketDetails.eticketsafetyorvdu}
                  onChange={(event) => handleEnteredValues('eticketsafetyorvdu', event.target.value)} />
                <SelectWithLabel label="Lens Material" options={lensMaterials} value={ticketDetails.eticketlensmaterial} onChange={(event) => handleEnteredValues('eticketlensmaterial', event.target.value)} />
                <SelectWithLabel missing={missingFields.eticket_lenstype} label={`Lens Type ${account.lentype_force === 0 ? "(Would you like to predefine the lens type for your employee? If not, you can leave this for the optician to decide)" : ""}`} required={(account.lentype_force === 0 ? false : true)} options={lensTypes} value={ticketDetails.eticket_lenstype} onChange={(event) => handleEnteredValues('eticket_lenstype', event.target.value)} />
                <SelectWithLabel
                  missing={missingFields.eticket_frame}
                  label={`Frame ${account.frameselect === 0 ? "(If you know the frame your employee requires, you can pre-select the frame, this is not essential)" : ""}`}
                  required={(account.frameselect === 0 ? false : true)}
                  options={frames}
                  value={ticketDetails.eticket_frame}
                  onChange={(event) => handleFrameChange(event.target.value)} />
                {optionalState.userSelectedFrame &&
                  <CheckboxWithLabel
                    label="Do you want to prevent the Optician from changing this frame option?"
                    checked={ticketDetails.eticket_lockframe}
                    onChange={(event) => handleEnteredValues('eticket_lockframe', event.target.checked ? 1 : 0)}
                  />
                }
              </div>
            }
            {currentStep === 2 &&
              <form>
                <Heading>Employee Details</Heading>
                <Paragraph>Please provide details of the employee you are raising this EyeTicket on behalf of and complete all required fields indicated by an asterisk <span style={{ color: '#880808' }}> *</span></Paragraph>
                <Row>
                  <FieldContainer>
                    <SelectWithLabel
                      label="Title"
                      options={titles}
                      missing={missingFields.etickettitle}
                      required={true}
                      value={ticketDetails.etickettitle}
                      onChange={(event) => handleEnteredValues('etickettitle', event.target.value)}
                      tabIndex={1} />
                  </FieldContainer>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='Department'
                      value={ticketDetails.eticketdepartment}
                      onChange={(event) => handleEnteredValues('eticketdepartment', event.target.value)}
                      required={(account.department_req === 0 ? false : true)}
                      missing={missingFields.eticketdepartment}
                      invalidData={invalidData?.eticketdepartment}
                      tabIndex={5} />
                  </FieldContainer>
                </Row>
                <Row>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='First name'
                      missing={missingFields.eticketchrist}
                      required={true}
                      value={ticketDetails.eticketchrist}
                      onChange={(event) => handleEnteredValues('eticketchrist', event.target.value)}
                      invalidData={invalidData?.eticketchrist}
                      tabIndex={2} />
                  </FieldContainer>
                  <FieldContainer>
                    <FlexContainer>
                      {account.ordernumber_prefix !== '-' && (
                        <TextInputWithLabel label='Prefix' width="30%" value={account.ordernumber_prefix} disabled />
                      )}
                      <TextInputWithLabel
                        label='SAP / Order Number'
                        value={ticketDetails.eticketorderno}
                        onChange={(event) => handleEnteredValues('eticketorderno', event.target.value)}
                        required={(account.ordernumber === 0 ? false : true)}
                        missing={missingFields.eticketorderno}
                        invalidData={invalidData?.eticketorderno}
                        disabled={account.contract_no !== "0"}
                        tabIndex={6} />
                    </FlexContainer>
                  </FieldContainer>
                </Row>
                <Row>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='Surname'
                      missing={missingFields.eticketsurn}
                      required={true}
                      value={ticketDetails.eticketsurn}
                      onChange={(event) => handleEnteredValues('eticketsurn', event.target.value)}
                      invalidData={invalidData?.eticketsurn}
                      tabIndex={3} />
                  </FieldContainer>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='Name Of Site'
                      value={ticketDetails.eticketsite}
                      onChange={(event) => handleEnteredValues('eticketsite', event.target.value)}
                      required={(account.nameofsite_req === 0 ? false : true)}
                      missing={missingFields.eticketsite}
                      invalidData={invalidData?.eticketsite}
                      tabIndex={7} />
                  </FieldContainer>
                </Row>
                <Row>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='Clock No'
                      value={ticketDetails.eticketclockno}
                      onChange={(event) => handleEnteredValues('eticketclockno', event.target.value)}
                      required={(account.clockno_req === 0 ? false : true)}
                      missing={missingFields.eticketclockno}
                      invalidData={invalidData?.eticketclockno}
                      tabIndex={4} />
                  </FieldContainer>
                  <FieldContainer>
                    <TextInputWithLabel
                      label='Cost Centre'
                      value={ticketDetails.eticketcostcent}
                      onChange={(event) => handleEnteredValues('eticketcostcent', event.target.value)}
                      required={(account.costcentre_req === 0 ? false : true)}
                      missing={missingFields.eticketcostcent}
                      invalidData={invalidData?.eticketcostcent}
                      tabIndex={8} />
                  </FieldContainer>
                </Row>
                <Alert type='info'>A confirmation SMS or email will be sent to the employee if you provide their mobile phone number or email address below. We will not use the employee's details for marketing purposes.</Alert>
                <Row>
                  <FieldContainer>
                    <SelectWithLabel
                      label="EyeTicket delivered by"
                      options={mobileOrEmail}
                      value={contactPref}
                      onChange={(e) => {
                        setContactPref(e.target.value);
                        setInvalidData(null);
                      }}
                    />
                  </FieldContainer>

                  <TextInputWithLabel
                    label={contactPref === 'Email' ? `Employee's email address` : `Employee's mobile phone number`}
                    invalidData={invalidContactData}
                    value={textInputContactField}
                    onChange={(event) => handleEnteredValues((contactPref === 'Email' ? 'eticketpatientemail' : 'eticketpatientmobile'), event.target.value)}
                  />
                </Row>
                <Row>
                  <FieldContainer>
                    <SelectWithLabel
                      label="Account"
                      options={linkedAccounts}
                      required={true}
                      value={ticketDetails.eticketaccountcode}
                      onChange={(event) => handleEnteredValues('eticketaccountcode', event.target.value)}
                      missing={missingFields.eticketaccountcode}
                    />
                  </FieldContainer>
                </Row>
              </form>
            }
            {currentStep === 3 &&
              <div>
                {account.marketresearch_optinout !== 1 && <><br></br> <Alert type='warning'>In order to raise an EyeTicket, you must first agree to our privacy policy which can be found within the Profile Preferences section</Alert></>}
                <Heading>Select an Optician</Heading>
                <Paragraph>
                  Please select an optician from the list below {isMobile ? "." : ". Note that opticians' fees are subject to change and are beyond the control of MCR Safety. We pass through all opticians' fees at cost and do not mark up these fees."}</Paragraph>
                <Table
                  columns={columns}
                  data={opticians}
                  initialState={initialState}
                  mobilePageSize={2}
                  setPostCodeFilter={setPostcode}
                  postcodeValue={postcode}
                  isValidPc={isValidPostcode}
                  colours={colours}
                  vhValue={"40vh"}
                  info={"No Opticians were found using the above search criteria"}
                  type={"warning"} />
              </div>
            }
          </ModalBody>
        }
        <ModalFooter>

          {Object.values(missingFields).some(isMissing => isMissing) &&
            (currentStep === 3 ?
              <Alert type='warning'>Please select an optician from the list above.</Alert>

              :
              <Alert type='warning'>Please populate all required fields indicated by an asterisk <span style={{ color: '#880808' }}> *</span></Alert>
            )
          }
          {isLoading && <Alert type='info'>Loading ..</Alert>}
          {currentStep > 0 && <Button onClick={prevStep} disabled={isLoading}>Back</Button>}
          {currentStep < stepTitles.length - 1 ? (
            <Button onClick={nextStep} disabled={isLoading}>Next</Button>
          ) : (
            <Button
              onClick={handleSaveEyeTicket}
              title={account.marketresearch_optinout !== 1 && "In order to raise an EyeTicket, you must first agree to our privacy policy which can be found within the Profile Preferences section"}
              disabled={isLoading || account.marketresearch_optinout !== 1}>{currentStep === 99 ? "✓ Submit EyeTicket with duplicate Clock No" : "✓ Submit EyeTicket"}</Button>
          )}

        </ModalFooter>
      </Container>
    </Wrapper>
  );
}
